import React, { useCallback, useContext, useEffect } from 'react';
import type { FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';
import { subDays } from 'date-fns';

import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import { token } from '@atlaskit/tokens';
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ButtonItem } from '@atlaskit/menu';

import { useIsFUMCEnabled } from '@confluence/premium-trial/entry-points/useIsFUMCEnabled';
import { getPercentageUsage } from '@confluence/premium-trial/entry-points/getPercentageUsage';
import { useSessionData } from '@confluence/session-data';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
	TRIAL_COMPONENTS_PREMIUM_INSIGHTS_ACTION_MENU_ITEM_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker';
import {
	Attribution,
	ErrorDisplay,
	TransparentErrorBoundary,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { isGraphQLError } from '@confluence/network';
import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';

import { PremiumTrialDetailsQuery } from '../graphql/PremiumTrialDetailsQuery.graphql';
import type { PremiumTrialDetailsQuery as PremiumTrialDetailsQueryType } from '../graphql/__types__/PremiumTrialDetailsQuery';

import { PopupMenuButtonText } from './PopupMenuButton';

const i18n = defineMessages({
	reviewPremiumInsights: {
		id: 'trial-components.premium.review-premium-insights.button',
		defaultMessage: 'Review Premium insights',
		description:
			'Review premium insights button that take the user to premium insights in mission control',
	},
	reviewPremiumInsightsHighUsageDescription: {
		id: 'trial-components.premium.review-premium-insights.high-usage.description',
		defaultMessage: '{featureUsagePercentage, number, percent} of users tried a Premium feature',
		description: 'Percentage of users who tried a Premium feature',
	},
	reviewPremiumInsightsLowDescription: {
		id: 'trial-components.premium.review-premium-insights.low-usage.description',
		defaultMessage: 'See how many people tried a Premium feature',
		description: 'Message that directs user to see how many people tried a Premium feature',
	},
});

type PremiumInsightsButtonProps = {
	trialEndDate?: string;
};

const premiumInsightsButtonDescriptionStyles = xcss({
	color: 'color.text.subtlest',
	fontSize: '12px',
	lineHeight: '12px',
});

export const MISSION_CONTROL_LINK = '/wiki/admin/center';

const startTime = subDays(new Date(), 30).toISOString();

export const PremiumInsightsButtonComponent: FC<PremiumInsightsButtonProps> = ({
	trialEndDate,
}) => {
	const { isLoggedIn } = useSessionData();
	const { isFUMCEnabled } = useIsFUMCEnabled({ trialEndDate });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		data: PremiumTrialDetailsData,
		error: PremiumTrialDetailsError,
		loading: PremiumTrialDetailsLoading,
	} = useQuery<PremiumTrialDetailsQueryType>(PremiumTrialDetailsQuery, {
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		variables: {
			startTime,
		},
		skip: !isLoggedIn || !isFUMCEnabled,
	});

	useEffect(() => {
		experienceTracker.start({
			name: TRIAL_COMPONENTS_PREMIUM_INSIGHTS_ACTION_MENU_ITEM_VIEW_EXPERIENCE,
		});
	}, [experienceTracker]);

	const premiumUsage = getPercentageUsage(PremiumTrialDetailsData);

	useEffect(() => {
		isFUMCEnabled &&
			!PremiumTrialDetailsLoading &&
			!PremiumTrialDetailsError &&
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'rendered',
					actionSubject: 'reviewPremiumInsightsMCCTA',
					source: 'premiumInsightsButtonMC',
					...(premiumUsage && { attributes: { premiumUsage } }),
				},
			}).fire();
	}, [
		PremiumTrialDetailsError,
		PremiumTrialDetailsLoading,
		createAnalyticsEvent,
		isFUMCEnabled,
		premiumUsage,
	]);

	const onClickPremiumInsights = useCallback(() => {
		isFUMCEnabled &&
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'reviewPremiumInsightsMCCTA',
					source: 'premiumInsightsButtonMC',
					...(premiumUsage && { attributes: { premiumUsage } }),
				},
			}).fire();
		window.open(MISSION_CONTROL_LINK, '_blank', 'noreferrer');
	}, [createAnalyticsEvent, isFUMCEnabled, premiumUsage]);

	if (PremiumTrialDetailsLoading) {
		return null;
	}

	if (!isFUMCEnabled) {
		experienceTracker.abort({
			name: TRIAL_COMPONENTS_PREMIUM_INSIGHTS_ACTION_MENU_ITEM_VIEW_EXPERIENCE,
			reason: 'Instance is not eligible to see premium feature insights',
		});
		return null;
	}

	if (PremiumTrialDetailsError) {
		experienceTracker.stopOnError({
			name: TRIAL_COMPONENTS_PREMIUM_INSIGHTS_ACTION_MENU_ITEM_VIEW_EXPERIENCE,
			error: PremiumTrialDetailsError,
		});
		if (
			(isUnauthorizedError(PremiumTrialDetailsError) || isGraphQLError(PremiumTrialDetailsError)) &&
			!isErrorMarkedAsHandled(PremiumTrialDetailsError)
		) {
			markErrorAsHandled(PremiumTrialDetailsError);
			return null;
		}
		return <ErrorDisplay error={PremiumTrialDetailsError} />;
	}

	return (
		<>
			<ButtonItem
				iconBefore={
					<GraphBarIcon
						label=""
						testId="Premium"
						size="small"
						primaryColor={token('color.icon', '#44546f')}
					/>
				}
				iconAfter={
					<ChevronRightIcon
						label=""
						testId="ChevronRight"
						size="medium"
						primaryColor={token('color.icon', '#44546F')}
					/>
				}
				onClick={onClickPremiumInsights}
				aria-haspopup
			>
				<PopupMenuButtonText>
					<FormattedMessage {...i18n.reviewPremiumInsights} />
					<Box xcss={premiumInsightsButtonDescriptionStyles}>
						{premiumUsage > 0.2 ? (
							<FormattedMessage
								{...i18n.reviewPremiumInsightsHighUsageDescription}
								values={{ featureUsagePercentage: premiumUsage }}
							/>
						) : (
							<FormattedMessage {...i18n.reviewPremiumInsightsLowDescription} />
						)}
					</Box>
				</PopupMenuButtonText>
			</ButtonItem>
			<ExperienceSuccess
				name={TRIAL_COMPONENTS_PREMIUM_INSIGHTS_ACTION_MENU_ITEM_VIEW_EXPERIENCE}
			/>
		</>
	);
};

export const PremiumInsightsButton: FC<PremiumInsightsButtonProps> = ({ trialEndDate }) => (
	<TransparentErrorBoundary attribution={Attribution.EDITIONS}>
		<PremiumInsightsButtonComponent trialEndDate={trialEndDate} />
	</TransparentErrorBoundary>
);
